import { motion } from "framer-motion";
import { Boxes, DollarSign, Sparkles, Truck, LayoutGrid, Star } from "lucide-react";

const steps = [
  {
    n: "01",
    icon: Boxes,
    title: "The Full Sort",
    text: "We move everything out to categorize items into \"Keep,\" \"Junk,\" and \"Buyout.\"",
    featured: false,
  },
  {
    n: "02",
    icon: DollarSign,
    title: "The Buy-Back Appraisal",
    text: "We identify tools, bikes, and gear we want to buy and offer you instant cash or service credit.",
    featured: true,
  },
  {
    n: "03",
    icon: Sparkles,
    title: "The Deep Scrub",
    text: "We remove cobwebs, wipe baseboards, and sweep the entire floor to prep for a fresh start.",
    featured: false,
  },
  {
    n: "04",
    icon: Truck,
    title: "The Haul-Away",
    text: "We load the junk and ensure it is disposed of or donated responsibly.",
    featured: false,
  },
  {
    n: "05",
    icon: LayoutGrid,
    title: "The Reset",
    text: "We move your \"Keep\" items back into a tidy, organized, and functional layout.",
    featured: false,
  },
];

export const HowItWorks = () => (
  <section id="how" className="bg-background py-12 md:py-16">
    <div className="container">
      <div className="mb-16 text-center">
        <p className="mb-3 text-sm font-bold uppercase tracking-[0.3em] text-safety-deep">The Process</p>
        <h2 className="font-display text-5xl tracking-wide text-ink md:text-6xl">The Garage Kings Process</h2>
        <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
          Five steps that separate a true Garage Reset from a basic junk haul.
        </p>
      </div>

      <div className="relative">
        {/* Horizontal safety-orange line (desktop) */}
        <div className="pointer-events-none absolute left-0 right-0 top-6 hidden h-1 rounded-full bg-safety lg:block" aria-hidden="true" />
        {/* Vertical line (mobile) */}
        <div className="pointer-events-none absolute left-6 top-0 h-full w-1 rounded-full bg-safety lg:hidden" aria-hidden="true" />

        <ol className="grid grid-cols-1 gap-8 lg:grid-cols-5 lg:gap-6">
          {steps.map((s, i) => (
            <motion.li
              key={s.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="relative pl-20 lg:pl-0 lg:pt-20"
            >
              {/* Node on the line */}
              <div className="absolute left-6 top-6 -translate-x-1/2 lg:left-1/2 lg:top-0">
                <div
                  className={`flex h-12 w-12 items-center justify-center rounded-full border-4 border-background ${
                    s.featured ? "bg-safety shadow-safety animate-pulse-gold" : "bg-safety"
                  }`}
                >
                  <s.icon className="h-5 w-5 text-white" />
                </div>
              </div>

              <div
                className={`relative h-full rounded-xl border p-6 transition-all ${
                  s.featured
                    ? "border-safety bg-slate-card text-slate-card-foreground shadow-safety"
                    : "border-white/10 bg-slate-card text-slate-card-foreground hover:border-safety/60"
                }`}
              >
                {s.featured && (
                  <div className="absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-full bg-paid-yellow px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-ink">
                    <Star className="h-3 w-3 fill-current" />
                    You Get Paid
                  </div>
                )}
                <div className="flex items-center gap-3 lg:flex-col lg:items-start lg:gap-1">
                  <span className="font-display text-4xl leading-none text-safety">{s.n}</span>
                  <h3 className="font-display text-2xl tracking-wide md:text-3xl lg:text-2xl">{s.title}</h3>
                </div>
                <p className="mt-3 text-sm text-white/75 md:text-base lg:text-sm">{s.text}</p>
              </div>
            </motion.li>
          ))}
        </ol>
      </div>

      <div className="mx-auto mt-16 max-w-md text-center">
        <div className="mx-auto mb-4 h-px w-32 bg-gradient-to-r from-transparent via-safety to-transparent" />
        <p className="font-semibold tracking-wide text-ink">
          A true reset takes <span className="text-safety-deep font-bold">2–6 hours</span>. Same-week service available.
        </p>
      </div>
    </div>
  </section>
);
